/**
 * MetaItem — one inline meta fact (tiny icon + text), NO pill box.
 *
 * The canonical replacement for the "boxed pill-chips everywhere" anti-pattern
 * (see the ando-ui-review skill: "pencil = less boxing"). Meta like sector /
 * location / size / tags read as space-separated inline items, not filled
 * chips. Pair several inside a ``<MetaRow>`` (flex-wrap, gap-x-4).
 *
 * Idle ink-3 text with an ink-4 icon — no background, no border. If you need a
 * clickable meta (e.g. a domain link) pass ``href``; it stays inline and only
 * gains an orange hover.
 */
import type * as React from "react";

import { cn } from "../../lib/cn";

export interface MetaItemProps {
  /** Small leading glyph (Lucide, ~13px) — rendered in ink-4. */
  readonly icon?: React.ReactNode;
  readonly children: React.ReactNode;
  /** When set, renders an anchor (opens in a new tab) instead of a span. */
  readonly href?: string;
  readonly className?: string;
}

export const MetaItem: React.FC<MetaItemProps> = ({ icon, children, href, className }) => {
  const base = cn(
    "inline-flex items-center gap-1.5 text-[12px] text-ink-3",
    href && "transition-colors hover:text-ando-orange",
    className,
  );
  const inner = (
    <>
      {icon ? (
        <span className="shrink-0 text-ink-4" aria-hidden>
          {icon}
        </span>
      ) : null}
      <span className="min-w-0 truncate">{children}</span>
    </>
  );
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={base}>
        {inner}
      </a>
    );
  }
  return <span className={base}>{inner}</span>;
};

MetaItem.displayName = "MetaItem";

/** Flex-wrap container for a row of {@link MetaItem}s (space-separated meta). */
export const MetaRow: React.FC<{
  readonly children: React.ReactNode;
  readonly className?: string;
}> = ({ children, className }) => (
  <div className={cn("flex flex-wrap items-center gap-x-4 gap-y-1.5", className)}>{children}</div>
);

MetaRow.displayName = "MetaRow";
